//   ____                       _ _
//  / ___|  ___  ___ _   _ _ __(_) |_ _   _
//  \___ \ / _ \/ __| | | | '__| | __| | | |
//   ___) |  __/ (__| |_| | |  | | |_| |_| |
//  |____/ \___|\___|\__,_|_|  |_|\__|\__, |
//                                    |___/
import Vue from 'vue'
import axios from 'axios'
import { getGermanHttpErrorMessage } from '@/helpers/httpErrorMessages'

// JWT payload is Base64URL encoded and contains UTF-8 JSON.
// atob() returns a binary (latin1) string, so we must decode it as UTF-8.
function base64UrlToUint8Array (base64Url) {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '==='.slice((base64.length + 3) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function decodeUtf8 (bytes) {
  if (typeof TextDecoder === 'function') {
    return new TextDecoder('utf-8').decode(bytes)
  }

  const encodedBytes = Array.from(bytes)
    .map((byte) => `%${byte.toString(16).padStart(2, '0')}`)
    .join('')

  return decodeURIComponent(encodedBytes)
}

export function decodeJwtPayload (token) {
  if (!token || typeof token !== 'string' || token.split('.').length < 2) {
    return null
  }

  try {
    const bytes = base64UrlToUint8Array(token.split('.')[1])
    const json = decodeUtf8(bytes)
    const payload = JSON.parse(json)

    return payload && typeof payload === 'object' ? payload : null
  } catch (error) {
    return null
  }
}

const storedToken = localStorage.getItem('token') || ''
const storedUser = decodeJwtPayload(storedToken)
const storedBaseUserToken = localStorage.getItem('baseUserToken') || null
const storedBaseUser = decodeJwtPayload(storedBaseUserToken)

if (storedToken && !storedUser) {
  localStorage.removeItem('token')
}
if (storedBaseUserToken && !storedBaseUser) {
  localStorage.removeItem('baseUserToken')
}

export default {
  namespaced: true,
  state: {
    error: null,
    isAuthenticated: Boolean(storedUser),
    switchedFrom: null,
    switched: false,
    token: storedUser ? storedToken : '',
    user: storedUser,
    baseUser: storedBaseUser,
    baseUserToken: storedBaseUser ? storedBaseUserToken : null
  },
  getters: {
    hasError (state) {
      return state.error !== null
    },
    error (state) {
      return state.error
    },
    user (state) {
      return state.user
    },
    baseUser (state) {
      return state.baseUser
    },
    baseUserToken (state) {
      return state.baseUserToken
    },
    isAuthenticated (state) {
      return Boolean(state.token && state.user)
    },
    isSwitched (state, getters) {
      return Boolean(
        getters.baseUser &&
        getters.user &&
        getters.baseUser.userid !== getters.user.userid
      )
    },
    hasRole: (state) => (role) => {
      return Boolean(
        state.user &&
        Array.isArray(state.user.roles) &&
        state.user.roles.includes(role)
      )
    }
  },
  mutations: {
    AUTHENTICATING_SUCCESS: function (state, token) {
      const jwtData = decodeJwtPayload(token)
      if (!jwtData) {
        state.error = 'Die Anmeldung konnte nicht wiederhergestellt werden. Bitte erneut anmelden.'
        state.isAuthenticated = false
        state.token = ''
        state.user = null
        localStorage.removeItem('token')
        return
      }

      state.error = null
      state.isAuthenticated = true
      state.token = token
      state.user = jwtData
      if (state.baseUserToken == null || state.baseUser == null) {
        state.baseUserToken = token
        localStorage.setItem('baseUserToken', token)
        state.baseUser = jwtData
      }

      localStorage.setItem('token', token)
    },
    AUTHENTICATING_ERROR: function (state, error) {
      state.error = error
      state.isAuthenticated = false
      localStorage.removeItem('token')
      state.token = ''
      state.user = null
    },
    AUTHENTICATING_LOGOUT: function (state) {
      state.error = null
      state.isAuthenticated = false
      state.token = ''
      state.user = null
      state.baseUserToken = null
      state.baseUser = null
      localStorage.removeItem('token')
      localStorage.removeItem('baseUserToken')
      delete axios.defaults.headers.common.authorization
    }
  },
  actions: {
    AUTH_LOGIN: ({ commit, dispatch }, user) => {
      return Vue.axios.post('/auth/login', {
        username: user.username,
        password: user.password
      }).then(function (response) {
        // Erfolgreiche Authentifizierung
        commit('AUTHENTICATING_SUCCESS', response.data.token)
      })
        .catch(function (error) {
          // Es soll trotz eines 401 den Error aus dem Response Body angezeigt werden
          if (error.response && error.response.status === 401) {
            commit('AUTHENTICATING_ERROR', 'Die Anmeldung ist fehlgeschlagen. Bitte E-Mail-Adresse und Passwort prüfen.')
          } else {
            // Anderer HTTP-Status-Code (zum Beispiel 500)
            commit('AUTHENTICATING_ERROR', getGermanHttpErrorMessage(error))
          }
          return Promise.reject(error)
        })
    },
    AUTH_USER_SWITCH: ({ commit, dispatch }, switchTo, switchToName) => {
      return Vue.axios.get('userswitch/' + switchTo).then(function (response) {
        // Erfolgreiche Authentifizierung
        commit('AUTHENTICATING_SUCCESS', response.data.token, true)
        return true
      })
        .catch(function (error) {
          // Es soll trotz eines 401 den Error aus dem Response Body angezeigt werden
          if (error.response && error.response.status === 401) {
            const data = error.response.data
            if (Vue._.has(data, 'message')) {
              commit('AUTHENTICATING_ERROR', data.message)
            } else {
              // Sollte nicht eintreten, malformed json response
              commit('AUTHENTICATING_ERROR', error)
            }
          } else {
            // Anderer HTTP-Status-Code (zum Beispiel 500)
            commit('AUTHENTICATING_ERROR', error)
          }
        })
    },
    AUTH_USER_SWITCH_EXIT: ({ commit, dispatch, getters }) => {
      return Vue.axios.get('userswitch/' + getters.baseUser.userid).then(function (response) {
        // Erfolgreiche Authentifizierung
        console.log(response.data.token)
        commit('AUTHENTICATING_SUCCESS', response.data.token, null)
      })
        .catch(function (error) {
          // Es soll trotz eines 401 den Error aus dem Response Body angezeigt werden
          if (error.response && error.response.status === 401) {
            const data = error.response.data
            if (Vue._.has(data, 'message')) {
              commit('AUTHENTICATING_ERROR', data.message)
            } else {
              // Sollte nicht eintreten, malformed json response
              commit('AUTHENTICATING_ERROR', error)
            }
          } else {
            // Anderer HTTP-Status-Code (zum Beispiel 500)
            commit('AUTHENTICATING_ERROR', error)
          }
        })
    },
    AUTH_LOGOUT: ({ commit, dispatch }) => {
      return commit('AUTHENTICATING_LOGOUT')
    }
  }
}
